import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { 
  FileText, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  XCircle,
  MessageCircle,
  Loader,
  RefreshCw
} from 'lucide-react'
import { Bid, Rfq, BidType, BidStatus, RfqType, RfqStatus } from '@/types'
import { formatCurrency, formatDate } from '@/utils'
import { tradingService } from '@/services/tradingService'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'

interface InquiriesTableProps {
  className?: string
  isMobile?: boolean
}

/**
 * A bid or an RFQ, discriminated so the two can be rendered in one list.
 *
 * Exported because the organization member-activity view renders the same merged
 * shape from `GET /organizations/{orgId}/members/{userId}/inquiries`, whose bid
 * and RFQ blocks are produced by the very same backend helpers as `/bids` and
 * `/rfq`.
 */
export type InquiryItem = (Bid & { inquiryType: 'bid' }) | (Rfq & { inquiryType: 'rfq' })

/**
 * Status -> badge label/style/icon. Hoisted to module scope (and exported) so the
 * organization member-activity table shows identical badges without duplicating
 * the mapping.
 */
export const getStatusMeta = (inquiry: InquiryItem) => {
  const rawStatus = (inquiry as any).status
  const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : rawStatus

  switch (status) {
    case RfqStatus.ACCEPTED:
    case BidStatus.ACCEPTED:
    case 'ACCEPTED':
      return {
        label: 'Accepted',
        badgeClass: 'border-[#1fb845] bg-[rgba(31,184,69,0.08)] text-[#188f36]',
        icon: CheckCircle,
      }
    case RfqStatus.REJECTED:
    case BidStatus.REJECTED:
    case 'REJECTED':
      return {
        label: 'Rejected',
        badgeClass: 'border-[#ff540c] bg-[rgba(255,84,12,0.08)] text-[#d74404]',
        icon: XCircle,
      }
    case RfqStatus.CONTACTED:
    case BidStatus.CONTACTED:
    case 'CONTACTED':
      return {
        label: 'Contacted',
        badgeClass: 'border-[#3b82f6] bg-[rgba(59,130,246,0.08)] text-[#2563eb]',
        icon: MessageCircle,
      }
    case RfqStatus.PENDING:
    case BidStatus.PENDING:
    case 'PENDING':
    default:
      return {
        label: 'Pending',
        badgeClass: 'border-[#ff8e00] bg-[rgba(255,142,0,0.06)] text-[#c26c00]',
        icon: Clock,
      }
  }
}

/** Buy/Sell label for either inquiry kind. */
export const getInquiryTypeLabel = (inquiry: InquiryItem) => {
  const type = inquiry.type
  return type === BidType.BUY || type === RfqType.BUY ? 'Buy' : 'Sell'
}

/** Stable row key across the two inquiry kinds. */
export const getInquiryKey = (inquiry: InquiryItem) =>
  inquiry.inquiryType === 'bid' ? inquiry.inquiry_id : inquiry.rfq_id

const InquiriesTable: React.FC<InquiriesTableProps> = ({ className, isMobile = false }) => {
  const [inquiries, setInquiries] = useState<InquiryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean
    inquiry: InquiryItem | null
    loading: boolean
  }>({
    isOpen: false,
    inquiry: null,
    loading: false
  })

  useEffect(() => {
    const fetchInquiries = async () => {
      setLoading(true)
      try {
        // Fetch both bids and RFQs
        const [bidsResponse, rfqsResponse] = await Promise.all([
          tradingService.getUserBids(),
          tradingService.getUserRfqs()
        ])

        // Combine and transform the data
        const combinedInquiries: InquiryItem[] = [
          ...bidsResponse.data.map(bid => ({ ...bid, inquiryType: 'bid' as const })),
          ...rfqsResponse.data.map(rfq => ({ ...rfq, inquiryType: 'rfq' as const }))
        ]

        // Sort by creation date (newest first)
        combinedInquiries.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )

        setInquiries(combinedInquiries)
      } catch (err) {
        console.error('Failed to fetch inquiries:', err)
        setError('Failed to load inquiries')
      } finally {
        setLoading(false)
      }
    }

    fetchInquiries()
  }, [])

  const handleDeleteClick = (inquiry: InquiryItem) => {
    setDeleteModal({
      isOpen: true,
      inquiry,
      loading: false
    })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.inquiry) return

    setDeleteModal(prev => ({ ...prev, loading: true }))

    try {
      if (deleteModal.inquiry.inquiryType === 'bid') {
        await tradingService.deleteBid(deleteModal.inquiry.inquiry_id)
      } else {
        await tradingService.deleteRfq(deleteModal.inquiry.rfq_id)
      }

      // Remove from local state after a successful delete
      setInquiries(prev => prev.filter(item => {
        if (deleteModal.inquiry!.inquiryType === 'bid' && item.inquiryType === 'bid') {
          return item.inquiry_id !== deleteModal.inquiry!.inquiry_id
        } else if (deleteModal.inquiry!.inquiryType === 'rfq' && item.inquiryType === 'rfq') {
          return item.rfq_id !== deleteModal.inquiry!.rfq_id
        }
        return true
      }))

      setDeleteModal({
        isOpen: false,
        inquiry: null,
        loading: false
      })
    } catch (err) {
      console.error('Failed to delete inquiry:', err)
      setError('Failed to delete inquiry')
      setDeleteModal(prev => ({ ...prev, loading: false }))
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      inquiry: null,
      loading: false
    })
  }

  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center py-8">
          <Loader className="animate-spin mr-2" size={20} />
          <span>Loading inquiries...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <XCircle className="text-red-500 mx-auto mb-2" size={24} />
            <p className="text-red-500 mb-2">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline" size="sm">
              <RefreshCw size={16} className="mr-1" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {!isMobile && (
        <div className="border border-white bg-[rgba(255,255,255,0.32)] dark:border-[#1b1919] dark:bg-[rgba(23,23,23,0.42)]">
        <p className="px-8 pt-8 font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          My Inquiries
        </p>

        {inquiries.length === 0 ? (
          <div className="px-8 pb-8 pt-6 text-center">
            <FileText className="mx-auto mb-4 text-[rgba(45,41,35,0.4)] dark:text-[rgba(253,253,253,0.4)]" size={40} />
            <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              No inquiries yet
            </h3>
            <p className="mt-2 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              Your RFQs and bids will appear here once you create them.
            </p>
          </div>
        ) : (
          <div className="px-8 pb-8 pt-4">
            <div className="overflow-x-auto dark:bg-[rgba(10,10,10,0.2)]">
              <table className="w-full min-w-[1576px] border-collapse">
                <thead>
                  <tr className="h-10 border-b border-[rgba(45,41,35,0.12)] dark:border-[#0a0a0a] dark:bg-[rgba(28,28,28,0.32)]">
                    <th className="w-[200px] px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Type</th>
                    <th className="w-[328px] px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Project</th>
                    <th className="w-[200px] px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Amount</th>
                    <th className="w-[240px] px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Valuation</th>
                    <th className="w-[200px] px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Status</th>
                    <th className="w-[168px] px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Date</th>
                    <th className="w-[240px] px-4 text-left font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((inquiry, index) => {
                    const statusMeta = getStatusMeta(inquiry)
                    const StatusIcon = statusMeta.icon
                    const isStripedRow = index % 2 === 0
                    const inquiryModeLabel = inquiry.inquiryType === 'bid' ? 'Bid' : 'RFQ'
                    const isBuyType = inquiry.type === BidType.BUY || inquiry.type === RfqType.BUY

                    return (
                      <tr
                        key={getInquiryKey(inquiry)}
                        className={`${isStripedRow ? 'bg-[rgba(255,255,255,0.28)] dark:bg-[rgba(28,28,28,0.8)]' : 'bg-transparent'}`}
                      >
                        <td className="h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 dark:border-[#0a0a0a]">
                          <div className="flex items-center gap-2">
                            {isBuyType ? (
                              <TrendingUp size={16} className="text-[#1fb845]" />
                            ) : (
                              <TrendingDown size={16} className="text-[#ff540c]" />
                            )}
                            <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                              {getInquiryTypeLabel(inquiry)}
                            </span>
                            <span className="inline-flex items-center justify-center bg-[rgba(45,41,35,0.12)] px-1 py-[2px] font-plus-jakarta text-[12px] font-medium leading-[16px] text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]">
                              {inquiryModeLabel}
                            </span>
                          </div>
                        </td>

                        <td className="h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 dark:border-[#0a0a0a]">
                          <div className="flex items-center gap-[10px]">
                            {inquiry.logo ? (
                              <img
                                src={inquiry.logo}
                                alt={inquiry.project_name}
                                className="h-[26px] w-[26px] rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[rgba(45,41,35,0.12)] font-plus-jakarta text-[10px] font-semibold text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]">
                                {inquiry.project_name.slice(0, 2).toUpperCase()}
                              </div>
                            )}
                            <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                              {inquiry.project_name}
                            </span>
                          </div>
                        </td>

                        <td className="h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:border-[#0a0a0a] dark:text-[#fdfdfd]">
                          {formatCurrency(inquiry.tranche_size)}
                        </td>

                        <td className="h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:border-[#0a0a0a] dark:text-[#fdfdfd]">
                          {formatCurrency(inquiry.valuation)}
                        </td>

                        <td className="h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 dark:border-[#0a0a0a]">
                          <span className={`inline-flex items-center gap-1 border px-[10px] py-[6px] font-plus-jakarta text-[12px] font-medium leading-[16px] ${statusMeta.badgeClass}`}>
                            <StatusIcon size={12} />
                            {statusMeta.label}
                          </span>
                        </td>

                        <td className="h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:border-[#0a0a0a] dark:text-[#fdfdfd]">
                          {formatDate(inquiry.created_at)}
                        </td>

                        <td className="h-[68px] border-b border-[rgba(45,41,35,0.12)] px-4 dark:border-[#0a0a0a]">
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(inquiry)}
                            className="inline-flex items-center justify-center border border-[#ff540c] bg-[rgba(255,255,255,0.72)] px-4 py-[10px] font-plus-jakarta text-[14px] font-medium uppercase leading-[20px] text-[#ff540c] dark:bg-[rgba(10,10,10,0.72)]"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>
      )}

      {isMobile && (
        <div className="border border-white bg-[rgba(255,255,255,0.32)] dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <p className="px-3 pt-3 font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          My Inquiries
        </p>

        {inquiries.length === 0 ? (
          <div className="px-3 pb-6 pt-5 text-center">
            <FileText className="mx-auto mb-4 text-[rgba(45,41,35,0.4)] dark:text-[rgba(253,253,253,0.4)]" size={40} />
            <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              No inquiries yet
            </h3>
            <p className="mt-2 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              Your RFQs and bids will appear here once you create them.
            </p>
          </div>
        ) : (
          <div className="space-y-3 px-3 pb-3 pt-3">
            {inquiries.map((inquiry) => {
              const statusMeta = getStatusMeta(inquiry)
              const StatusIcon = statusMeta.icon
              const inquiryModeLabel = inquiry.inquiryType === 'bid' ? 'Bid' : 'RFQ'
              const isBuyType = inquiry.type === BidType.BUY || inquiry.type === RfqType.BUY

              return (
                <div
                  key={getInquiryKey(inquiry)}
                  className="border border-[rgba(45,41,35,0.06)] bg-white p-3 dark:border-[rgba(253,253,253,0.08)] dark:bg-[#1c1c1c]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex items-center gap-3">
                      {inquiry.logo ? (
                        <img
                          src={inquiry.logo}
                          alt={inquiry.project_name}
                          className="h-9 w-9 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(45,41,35,0.12)] font-plus-jakarta text-[11px] font-semibold text-[#2d2923] dark:bg-[rgba(253,253,253,0.16)] dark:text-[#fdfdfd]">
                          {inquiry.project_name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <span className="truncate font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {inquiry.project_name}
                      </span>
                    </div>

                    <div className="flex shrink-0 items-center gap-[8px]">
                      {isBuyType ? (
                        <TrendingUp size={16} className="text-[#1fb845]" />
                      ) : (
                        <TrendingDown size={16} className="text-[#ff540c]" />
                      )}
                      <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {getInquiryTypeLabel(inquiry)}
                      </span>
                      <span className="inline-flex items-center bg-[rgba(45,41,35,0.12)] px-1 py-[2px] font-plus-jakarta text-[12px] font-medium leading-[16px] text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]">
                        {inquiryModeLabel}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-3">
                    <div>
                      <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        Amount
                      </p>
                      <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {formatCurrency(inquiry.tranche_size)}
                      </p>
                    </div>
                    <div>
                      <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        Valuation
                      </p>
                      <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {formatCurrency(inquiry.valuation)}
                      </p>
                    </div>
                    <div>
                      <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        Date
                      </p>
                      <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {formatDate(inquiry.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        Status
                      </p>
                      <span className={`mt-[2px] inline-flex items-center gap-1 border px-[10px] py-[6px] font-plus-jakarta text-[12px] font-medium leading-[16px] ${statusMeta.badgeClass}`}>
                        <StatusIcon size={12} />
                        {statusMeta.label}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleDeleteClick(inquiry)}
                    className="mt-3 inline-flex w-full items-center justify-center border border-[#ff540c] bg-[rgba(255,255,255,0.72)] px-4 py-[10px] font-plus-jakarta text-[14px] font-medium uppercase leading-[20px] text-[#ff540c] dark:bg-[rgba(45,41,35,0.72)]"
                  >
                    Delete
                  </button>
                </div>
              )
            })}
          </div>
        )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Inquiry"
        message={`Are you sure you want to delete this ${deleteModal.inquiry?.inquiryType === 'bid' ? 'bid' : 'RFQ'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleteModal.loading}
      />
    </div>
  )
}

export default InquiriesTable
